import { Helmet } from 'react-helmet-async'
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useScrollReveal, fadeUpVariants, staggerContainerVariants } from '../hooks/useScrollReveal'
import { useAdmin } from '../context/AdminContext'
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaCheck, FaPaperPlane, FaExclamationCircle, FaWhatsapp } from 'react-icons/fa'

const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:4000/api"

function sanitize(str = '', max = 500) {
  return str.replace(/<[^>]*>/g, '').replace(/[<>"']/g, '').trim().slice(0, max)
}
function isValidEmail(e) {
  return /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(String(e).toLowerCase())
}

export default function Contact() {
  const { ref, controls } = useScrollReveal()
  const { siteData, loading } = useAdmin()
  const { contact } = siteData

  const [form,      setForm]      = useState({ name: '', email: '', message: '' })
  const [errors,    setErrors]    = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [apiError,  setApiError]  = useState('')

  if (loading) return null

  const contactInfo = [
    { icon: FaEnvelope,     label: 'Email',    value: contact.email    || '', href: contact.email    ? `mailto:${contact.email}`                          : null },
    { icon: FaPhone,        label: 'Phone',    value: contact.phone    || '', href: contact.phone    ? `tel:${(contact.phone).replace(/\s/g,'')}`          : null },
    { icon: FaWhatsapp,     label: 'WhatsApp', value: contact.phone    || '', href: contact.phone    ? `https://wa.me/${(contact.phone).replace(/\D/g,'')}` : null },
    { icon: FaMapMarkerAlt, label: 'Location', value: contact.location || '', href: null },
  ]

  const validate = () => {
    const e = {}
    if (!form.name.trim() || form.name.trim().length < 2)  e.name    = 'Please enter your name (min 2 chars)'
    if (!form.email.trim())                                  e.email   = 'Email is required'
    else if (!isValidEmail(form.email))                      e.email   = 'Enter a valid email address'
    if (!form.message.trim() || form.message.trim().length < 10) e.message = 'Message too short (min 10 chars)'
    return e
  }

  const handleChange = (field, val) => {
    setForm(p => ({ ...p, [field]: val }))
    if (errors[field]) setErrors(p => ({ ...p, [field]: '' }))
    if (apiError) setApiError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length) { setErrors(errs); return }

    setIsSubmitting(true)
    setApiError('')

    try {
      const res = await fetch(`${API_BASE}/send-email`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name:    sanitize(form.name, 100),
          email:   sanitize(form.email, 150),
          message: sanitize(form.message, 2000),
        }),
      })
      const data = await res.json()
      if (res.ok && data.success) {
        setSubmitted(true)
        setForm({ name: '', email: '', message: '' })
      } else {
        setApiError(data.message || 'Something went wrong. Please try again.')
      }
    } catch {
      setApiError('Network error. Please check your connection and try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section id="contact" className="py-28 relative overflow-hidden">
      <Helmet>
        <title>Contact Me | Shiv Shankar Pandit</title>
        <meta name="description" content="Get in touch. I'm always open to discussing new projects and opportunities." />
      </Helmet>
      
      {/* Ambient */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full blur-[130px] opacity-[0.06] pointer-events-none"
        style={{ background: 'radial-gradient(circle, var(--accent-1) 0%, transparent 70%)' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <motion.div variants={staggerContainerVariants} initial="hidden" animate="visible">

          {/* Header */}
          <motion.div variants={fadeUpVariants} className="mb-16 text-center">
            <h1 className="section-heading mb-4">
              Let's <span className="gradient-text">Connect</span>
            </h1>
            <p className="text-secondary text-base max-w-xl mx-auto leading-relaxed">{contact.callout}</p>
          </motion.div>

          <div className="grid lg:grid-cols-5 gap-8 items-start">

            {/* ── Left: contact info ── */}
            <motion.div variants={fadeUpVariants} className="lg:col-span-2 space-y-4">
              <h3 className="font-display font-bold text-xl text-primary mb-6">Get in Touch</h3>

              {contactInfo.map(({ icon: Icon, label, value, href }) => (
                <motion.div
                  key={label}
                  className="card p-5 flex items-center gap-4"
                  whileHover={{ y: -2, boxShadow: 'var(--shadow-glow)' }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(16,185,129,0.15))' }}>
                    <Icon size={16} style={{ color: 'var(--accent-1)' }} />
                  </div>
                  <div>
                    <p className="field-label" style={{ marginBottom: '2px' }}>{label}</p>
                    {href
                      ? <a href={href} rel="noopener noreferrer" className="text-sm font-medium text-primary hover:text-accent transition-colors">{value}</a>
                      : <p className="text-sm font-medium text-primary">{value}</p>
                    }
                  </div>
                </motion.div>
              ))}

              {/* CTA note */}
              <div className="card p-5 mt-2"
                style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.05), rgba(16,185,129,0.05))' }}>
                <p className="text-sm text-secondary leading-relaxed">
                  💡 <span className="font-medium text-primary">Looking for a developer?</span>{' '}
                  I'm actively seeking new opportunities and collaborations!
                </p>
              </div>
            </motion.div>

            {/* ── Right: form ── */}
            <motion.div variants={fadeUpVariants} className="lg:col-span-3">
              <AnimatePresence mode="wait">
                {submitted ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.92, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 18 }}
                    className="card p-12 flex flex-col items-center text-center gap-6"
                    style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.05), rgba(16,185,129,0.05))' }}
                  >
                    <motion.div
                      initial={{ scale: 0, rotate: -20 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: 'spring', stiffness: 220, damping: 14, delay: 0.1 }}
                      className="w-20 h-20 rounded-3xl flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg, #10b981, #10b981)', boxShadow: '0 0 40px rgba(16,185,129,0.3)' }}
                    >
                      <FaCheck size={28} className="text-white" />
                    </motion.div>
                    <div>
                      <h3 className="font-display font-bold text-2xl text-primary mb-2">Message Sent! 🎉</h3>
                      <p className="text-secondary text-sm leading-relaxed">
                        Thanks for reaching out! I've received your message and<br />
                        will get back to you within <strong className="text-accent">24 hours</strong>.
                      </p>
                    </div>
                    <motion.button
                      onClick={() => setSubmitted(false)}
                      className="btn-outline text-sm"
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      Send Another Message
                    </motion.button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    onSubmit={handleSubmit}
                    noValidate
                    className="card p-8 space-y-5"
                  >
                    {/* API error banner */}
                    <AnimatePresence>
                      {apiError && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm"
                          style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', color: '#f87171' }}
                        >
                          <FaExclamationCircle size={14} className="flex-shrink-0" />
                          {apiError}
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Name + Email row */}
                    <div className="grid sm:grid-cols-2 gap-5">
                      {['name','email'].map(field => (
                        <div key={field}>
                            <label htmlFor={field} className="field-label">{field} <span className="text-red-500 font-bold ml-0.5">*</span></label>
                          <input
                            id={field} name={field}
                            type={field === 'email' ? 'email' : 'text'}
                            value={form[field]}
                            onChange={e => handleChange(field, e.target.value)}
                            placeholder={field === 'email' ? 'your@email.com' : 'Your full name'}
                            maxLength={field === 'email' ? 150 : 100}
                            autoComplete={field === 'email' ? 'email' : 'name'}
                            className={`field-input ${errors[field] ? 'error' : ''}`}
                          />
                          {errors[field] && (
                            <p className="text-xs mt-1.5 font-mono" style={{ color: '#f87171' }}>{errors[field]}</p>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Message */}
                    <div>
                      <label htmlFor="message" className="field-label">message <span className="text-red-500 font-bold ml-0.5">*</span></label>
                      <textarea
                        id="message" name="message"
                        value={form.message}
                        onChange={e => handleChange('message', e.target.value)}
                        placeholder="Tell me about your project, idea, or opportunity..."
                        rows={6}
                        maxLength={2000}
                        className={`field-input resize-none ${errors.message ? 'error' : ''}`}
                      />
                      <div className="flex justify-between mt-1.5">
                        {errors.message
                          ? <p className="text-xs font-mono" style={{ color: '#f87171' }}>{errors.message}</p>
                          : <span />
                        }
                        <p className="text-xs font-mono text-muted">{form.message.length}/2000</p>
                      </div>
                    </div>

                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary w-full flex items-center justify-center gap-2.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
                      whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                      whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                    >
                      {isSubmitting ? (
                        <>
                          <motion.span
                            className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 0.75, repeat: Infinity, ease: 'linear' }}
                          />
                          Sending…
                        </>
                      ) : (
                        <>
                          <FaPaperPlane size={13} />
                          Send Message
                        </>
                      )}
                    </motion.button>

                    <p className="text-center font-mono text-xs text-muted">
                      Your message goes directly to my inbox 📬
                    </p>
                  </motion.form>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
